import Vue from 'vue';
import { mapState } from 'vuex';
import Panel from './components/panel';
import { SAFEA_V3_ENABLED } from '~/constants/_core/safea-v3';

export default Vue.extend({
  components: {
    Panel
  },
  props: {
    titleAccess : String,
    titleRole   : String,
    logo        : Boolean,
    subtitle    : String
  },
  data() {
    return {
      currentRole: {
        tenant : null,
        ies    : null,
        role   : null
      },
      accessActive: null
    };
  },
  computed: {
    ...mapState({
      currentRights     : (state) => state._core.auth.currentRole,
      tenantsRights     : (state) => state._core.auth.tenantsRights,
      applicationsRoles : (state) => state._core.auth.applicationsRoles,
      isOperation       : (state) => state._core.application.isOperation,
      owner             : (state) => state._core.application.owner
    }),
    currentTenantId: {
      get() {
        return this.currentRole?.tenant?.id;
      },
      set(value) {
        this.currentRole.tenant = this.tenants.find(tenant => tenant.id === value);
      }
    },
    currentIesId: {
      get() {
        return this.currentRole?.ies?.id;
      },
      set(value) {
        this.currentRole.ies = this.ies.find(ies => ies.id === value);
      }
    },
    access() {
      const access = [];

      if (this.applicationsRoles?.roles) {
        access.push({
          name     : this.$t('_core.roleSelection.application'),
          key      : 'application',
          subtitle : ''
        });
      }

      const hasTenantWithRole = this.tenants?.find(tenant => {
        return tenant.roles.length;
      });

      if (hasTenantWithRole) {
        access.push({
          name     : this.$t('_core.roleSelection.tenant'),
          key      : 'tenant',
          subtitle : this.tenants?.name ? `Tenant: ${this.tenants?.name}` : ''
        });

        if (access.length === 1 && this.owner === null) {
          access[0].subtitle = 'Tenant';
        }
      }

      const hasIesWithRole = this.tenants.find(tenant => {
        return tenant.ies.find(ies => {
          return ies.roles.length;
        });
      });

      if (hasIesWithRole) {
        let subtitle = `${this.$t('_core.roleSelection.ies')}: ${hasIesWithRole.ies[0]?.name}`;
        access.push({
          name     : this.$t('_core.roleSelection.ies'),
          key      : 'ies',
          subtitle : this._getSubtitle(hasIesWithRole.ies[0]?.name, subtitle)
        });

        if (access.length === 1 && this.owner === 'tenant') {
          subtitle = `${this.$t('_core.roleSelection.tenant')}: ${this.tenants[0].name}`;
          access[0].subtitle = this._getSubtitle(this.tenants[0].name, subtitle);
        }

      }

      return access;
    },
    tenants() {
      return this.tenantsRights.filter(tenant => {
        tenant.logoMini = this.$vuetify.theme.dark ? tenant.tenantLogoMiniThemeDark : tenant.tenantLogoMini;
        if (this.accessActive !== 'tenant' || tenant.roles.length) {
          return tenant;
        }
      })
        .filter(item => {
          return !(this.accessActive === 'ies' && !item.ies.length);
        });
    },
    ies() {
      return this.currentRole.tenant?.ies?.map(ies => {
        ies.logoMini = this.$vuetify.theme.dark ? ies.iesLogoMiniThemeDark : ies.iesLogoMini;
        return ies;
      }) || [];
    },
    roles() {
      let roles = [];

      switch (this.accessActive) {
        case 'application':
          roles = this.applicationsRoles?.roles || [];
          break;
        case 'tenant':
          roles = this.currentRole.tenant?.roles || [];
          break;
        case 'ies':
          roles = this.currentRole.ies?.roles || [];
          break;
      }

      if (roles) {
        roles.map(item => {
          item.text = item.name || item.alias;
          item.active = this.currentRights?.role?.id === item.id;
        });
      }

      return roles;
    },
    hasMoreOfOneAccess() {
      if (this.access.length === 1) {
        return this.access[0];
      }

      return null;
    }
  },
  mounted() {
    this.loadCurrentRoleActive();

    if (this.hasMoreOfOneAccess) {
      this.accessActive = this.hasMoreOfOneAccess.key;
    }
  },
  methods: {
    _getSubtitle(condition, subtitle) {
      return condition ? subtitle : '';
    },
    async activeRole(role) {
      if (role.active) {
        return;
      }

      const { tenant, ies } = this.currentRole;
      let access = {};

      switch (this.accessActive) {
        case 'application':
          access = { role };
          break;
        case 'tenant':
          access = { role, tenant };
          break;
        case 'ies':
          access = { role, tenant, ies };
          break;
      }

      this.$nuxt.$loading.start();
      try {
        if (SAFEA_V3_ENABLED) {
          await this.$store.dispatch('_core/auth/actionAssumeRoleV3', {
            tenantUuid      : access.tenant?.uuid,
            institutionUuid : access.ies?.uuid,
            v2RoleId        : role.roleId
          });
        } else {
          await this.$store.dispatch('_core/auth/actionAssumeRole', access);
          if (this.$route.path === '/') {
            window.location.href = window.origin + '/plataforma';
            // await this.$nuxt.refresh();
          } else {
            // Nuxt throws an error when redirecting twice (this redirection + one in the middleware)
            this.$router.replace({ path: '/', query: this.$route.query }).catch(() => { /**/ });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.$nuxt.$loading.finish();
      }

      if (this.hasMoreOfOneAccess) {
        this.accessActive = this.hasMoreOfOneAccess.key;
      } else {
        this.accessActive = null;
      }
    },
    loadCurrentRoleActive() {
      this.currentRole = {
        tenant : null,
        ies    : null,
        role   : null
      };

      if (this.tenants.length === 1) {
        this.currentRole.tenant = this.tenants[0];

        if (this.tenants[0].ies.length === 1) {
          this.currentRole.ies = this.tenants[0].ies[0];
        } else {
          this.currentRole.ies = this.tenants[0].ies;
        }
      }
    }
  },
  watch: {
    accessActive() {
      if (this.hasMoreOfOneAccess) {
        this.accessActive = this.hasMoreOfOneAccess.key;
      }

      this.loadCurrentRoleActive();
    },
    'currentRole.tenant'(tenant, oldTenant) {
      if (typeof oldTenant?.alias === 'string' && tenant?.alias !== oldTenant?.alias) {
        this.currentRole.ies = null;
      }
    }
  }
});
